import { z } from "zod";

import type { ComfyUIImagePayload } from "../types";
import { BRAND_MARKUP } from "../../constants";
import { defineModel } from "../types";

// Cost estimate: RTX 4090 at $0.34/hr, ~30s per generation = ~$0.003
const COST_PER_IMAGE_USD = 0.003;

const DEFAULT_NEGATIVE_PROMPT =
  "ugly, blurry, deformed, bad anatomy, extra limbs, extra fingers, mutated hands, fused fingers, too many fingers, long neck, malformed limbs, poorly drawn hands, poorly drawn face, disfigured, out of frame, watermark, text, signature";

const LORA_NAMES = ["realistic_leak", "realistic_liquid"] as const;
type LoraName = (typeof LORA_NAMES)[number];

const loraEntrySchema = z.object({
  name: z.enum(LORA_NAMES),
  strength: z.number().min(0).max(1).optional(),
});

// All valid SDXL latent resolutions (width x height).
// Includes standard 1024-range from SDXLEmptyLatentSizePicker+ and upscaled variants.
const ALLOWED_RESOLUTIONS = new Set([
  // 1024-range (from SDXLEmptyLatentSizePicker+)
  "704x1408",
  "704x1344",
  "768x1344",
  "768x1280",
  "832x1216",
  "832x1152",
  "896x1152",
  "896x1088",
  "960x1088",
  "960x1024",
  "1024x1024",
  "1024x960",
  "1088x960",
  "1088x896",
  "1152x896",
  "1152x832",
  "1216x832",
  "1280x768",
  "1344x768",
  "1344x704",
  "1408x704",
  "1472x704",
  "1536x640",
  "1600x640",
  "1664x576",
  "1728x576",
  // Upscaled variants (use 1.2x refinement; 1024-range uses 1.4x)
  "1536x1536",
  "1344x1664",
  "1664x1344",
  "1280x1920",
  "1920x1280",
  "1152x2048",
  "2048x1152",
]);

const UPSCALED_RESOLUTIONS = new Set([
  "1536x1536",
  "1344x1664",
  "1664x1344",
  "1280x1920",
  "1920x1280",
  "1152x2048",
  "2048x1152",
]);

const resolutionSchema = z
  .string()
  .regex(/^\d+x\d+$/, "Resolution must be WIDTHxHEIGHT (e.g. 1280x1920)")
  .refine((val) => ALLOWED_RESOLUTIONS.has(val), {
    message: `Invalid resolution. Allowed: ${[...ALLOWED_RESOLUTIONS].join(", ")}`,
  });

const cookieParams = z
  .object({
    prompt: z.string().default(""),
    negativePrompt: z.string().default(DEFAULT_NEGATIVE_PROMPT),
    seed: z.number().optional(),
    loras: z.array(loraEntrySchema).optional(),
    resolution: resolutionSchema.optional(),
  })
  .strip();

type WorkflowNode = { inputs: Record<string, unknown>; class_type: string };

function node(
  workflow: Record<string, unknown>,
  id: string,
): Record<string, unknown> {
  return (workflow[id] as WorkflowNode).inputs;
}

// Cookie workflow: Generate (KSamplerWithNAG, lcm, 8 steps) → 4x Upscale → Refine → Save
const COOKIE_WORKFLOW: Record<string, unknown> = {
  "4": {
    inputs: { ckpt_name: "lustify_apex_00001_.safetensors" },
    class_type: "CheckpointLoaderSimple",
  },
  "8": {
    inputs: { seed: -1 },
    class_type: "Seed (rgthree)",
  },
  "12": {
    inputs: { images: ["11:10", 0] },
    class_type: "PreviewImage",
  },
  "13": {
    inputs: {
      resolution: "896x1152 (0.78)",
      batch_size: 1,
      width_override: 1280,
      height_override: 1920,
    },
    class_type: "SDXLEmptyLatentSizePicker+",
  },
  "34": {
    inputs: { images: ["33:46", 0] },
    class_type: "PreviewImage",
  },
  "90": {
    inputs: { filename_prefix: "ComfyUI", images: ["33:46", 0] },
    class_type: "SaveImage",
  },
  "93": {
    inputs: {
      PowerLoraLoaderHeaderWidget: { type: "PowerLoraLoaderHeaderWidget" },
      lora_1: {
        on: true,
        lora: "speed/dmd2_sdxl_4step_lora.safetensors",
        strength: 1,
      },
      lora_2: {
        on: false,
        lora: "leaked_nudes_style_v1_fixed.safetensors",
        strength: 0.7,
      },
      lora_3: {
        on: false,
        lora: "realcumv6.55.safetensors",
        strength: 0.5,
      },
      "\u27A5 Add Lora": "",
      model: ["4", 0],
      clip: ["4", 1],
    },
    class_type: "Power Lora Loader (rgthree)",
  },
  "164": {
    inputs: { image: "example.png" },
    class_type: "LoadImage",
  },
  "11:10": {
    inputs: { samples: ["11:210", 0], vae: ["4", 2] },
    class_type: "VAEDecode",
  },
  "11:5": {
    inputs: { text: "", clip: ["93", 1] },
    class_type: "CLIPTextEncode",
  },
  "11:6": {
    inputs: { text: "", clip: ["93", 1] },
    class_type: "CLIPTextEncode",
  },
  "11:210": {
    inputs: {
      seed: ["8", 0],
      steps: 8,
      cfg: 1,
      nag_scale: 5,
      nag_tau: 2.5,
      nag_alpha: 0.3,
      nag_sigma_end: 0,
      sampler_name: "lcm",
      scheduler: "karras",
      denoise: 1,
      model: ["93", 0],
      positive: ["11:5", 0],
      negative: ["11:6", 0],
      nag_negative: ["11:6", 0],
      latent_image: ["13", 0],
    },
    class_type: "KSamplerWithNAG",
  },
  "33:26": {
    inputs: { value: 1.2 },
    class_type: "PrimitiveFloat",
  },
  "33:17": {
    inputs: { model_name: "4x_NMKD-Superscale-SP_178000_G.pth" },
    class_type: "UpscaleModelLoader",
  },
  "33:47": {
    inputs: { image: ["33:32", 0] },
    class_type: "GetImageSize+",
  },
  "33:20": {
    inputs: { image: ["33:21", 0] },
    class_type: "GetImageSize+",
  },
  "33:22": {
    inputs: { expression: "a/4", a: ["33:26", 0] },
    class_type: "MathExpression|pysssss",
  },
  "33:16": {
    inputs: { upscale_model: ["33:17", 0], image: ["11:10", 0] },
    class_type: "ImageUpscaleWithModel",
  },
  "33:21": {
    inputs: {
      upscale_method: "lanczos",
      scale_by: ["33:22", 1],
      image: ["33:16", 0],
    },
    class_type: "ImageScaleBy",
  },
  "33:46": {
    inputs: {
      width: ["33:47", 0],
      height: ["33:47", 1],
      interpolation: "lanczos",
      method: "fill / crop",
      condition: "always",
      multiple_of: 64,
      image: ["33:32", 0],
    },
    class_type: "ImageResize+",
  },
  "33:31": {
    inputs: { text: "", clip: ["93", 1] },
    class_type: "CLIPTextEncode",
  },
  "33:28": {
    inputs: { pixels: ["33:214", 0], vae: ["4", 2] },
    class_type: "VAEEncode",
  },
  "33:32": {
    inputs: { samples: ["33:211", 0], vae: ["4", 2] },
    class_type: "VAEDecode",
  },
  "33:211": {
    inputs: {
      seed: 0,
      steps: 7,
      cfg: 1,
      nag_scale: 5,
      nag_tau: 2.5,
      nag_alpha: 0.25,
      nag_sigma_end: 0,
      sampler_name: "lcm",
      scheduler: "karras",
      denoise: 0.55,
      model: ["93", 0],
      positive: ["33:30", 0],
      negative: ["33:31", 0],
      nag_negative: ["33:31", 0],
      latent_image: ["33:28", 0],
    },
    class_type: "KSamplerWithNAG",
  },
  "33:30": {
    inputs: { text: "", clip: ["93", 1] },
    class_type: "CLIPTextEncode",
  },
  "33:18": {
    inputs: {
      width: ["33:20", 0],
      height: ["33:20", 1],
      interpolation: "lanczos",
      method: "fill / crop",
      condition: "always",
      multiple_of: 64,
      image: ["33:21", 0],
    },
    class_type: "ImageResize+",
  },
  "33:214": {
    inputs: { amount: 0.2, image: ["33:18", 0] },
    class_type: "ImageCASharpening+",
  },
};

function buildWorkflow(params: {
  prompt: string;
  negativePrompt: string;
  seed: number;
  loras?: { name: LoraName; strength?: number }[];
  resolution?: string;
}): Record<string, unknown> {
  const workflow = structuredClone(COOKIE_WORKFLOW);

  // Inject prompt into both generation and refinement CLIP nodes
  node(workflow, "11:5").text = params.prompt;
  node(workflow, "33:30").text = params.prompt;

  // Inject negative prompt into both generation and refinement CLIP nodes
  node(workflow, "11:6").text = params.negativePrompt;
  node(workflow, "33:31").text = params.negativePrompt;

  // Inject seed (rgthree Seed node max is 1125899906842624)
  node(workflow, "8").seed = params.seed;
  // Refinement seed derived from main seed for reproducibility
  node(workflow, "33:211").seed = (params.seed + 1) % 1125899906842624;

  // Override latent resolution and adjust upscale factor based on resolution tier
  if (params.resolution) {
    const [width, height] = params.resolution.split("x").map(Number);
    node(workflow, "13").width_override = width;
    node(workflow, "13").height_override = height;
    // 1024-range resolutions get 1.4x refinement; larger ones keep 1.2x
    node(workflow, "33:26").value = UPSCALED_RESOLUTIONS.has(params.resolution)
      ? 1.2
      : 1.4;
  }

  // Content LoRAs (lora_2, lora_3) are off by default.
  // When loras is provided, enable the listed ones.
  if (params.loras) {
    const loraInputs = node(workflow, "93");
    const enabledByName = new Map(
      params.loras.map((entry) => [entry.name, entry.strength]),
    );

    if (enabledByName.has("realistic_leak")) {
      const lora = loraInputs.lora_2 as Record<string, unknown>;
      lora.on = true;
      const strength = enabledByName.get("realistic_leak");
      if (strength !== undefined) lora.strength = strength;
    }

    if (enabledByName.has("realistic_liquid")) {
      const lora = loraInputs.lora_3 as Record<string, unknown>;
      lora.on = true;
      const strength = enabledByName.get("realistic_liquid");
      if (strength !== undefined) lora.strength = strength;
    }
  }

  return workflow;
}

export const cookie = defineModel({
  key: "cookie",
  stepType: "image",
  spec: {
    label: "Cookie",
    description:
      "SDXL image generation for spicy content ONLY. Do NOT select this model unless the user explicitly asks for spicy content. Supports both tag-based (1cat, portrait) and natural language prompts.",
    tags: ["image-gen"],
    hidden: true,
    params: cookieParams,
  },
  providers: [
    {
      provider: "comfyui",
      modelId: "cookie",
      estimateCost() {
        return Math.ceil(COST_PER_IMAGE_USD * 1_000_000 * (1 + BRAND_MARKUP));
      },
      toPayload(params): ComfyUIImagePayload {
        // rgthree Seed node max is 1125899906842624
        const seed =
          params.seed ?? Math.floor(Math.random() * 1125899906842624);
        return {
          workflow: buildWorkflow({
            prompt: params.prompt,
            negativePrompt: params.negativePrompt ?? DEFAULT_NEGATIVE_PROMPT,
            seed,
            loras: params.loras,
            resolution: params.resolution,
          }),
        };
      },
    },
  ],
});
